import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ICexAccount } from 'types/accounts';

interface PairStatisticState {
  /**
   * Map of pairId to selected account IDs
   * Using IDs instead of full objects to avoid serialization issues
   */
  selectedAccountIdsByPair: Record<number, number[]>;

  /**
   * Set selected accounts for a specific pair
   */
  setSelectedAccounts: (pairId: number, accountIds: number[]) => void;

  /**
   * Get selected account IDs for a specific pair
   */
  getSelectedAccountIds: (pairId: number) => number[];

  /**
   * Clear selected accounts for a specific pair
   */
  clearSelectedAccounts: (pairId: number) => void;

  /**
   * Clear all selected accounts
   */
  clearAll: () => void;
}

const STORAGE_KEY = 'cex-pair-statistic-selected-accounts';

/**
 * Stable empty array reference to prevent infinite re-renders
 * when using `?? []` pattern with Zustand selectors
 */
const EMPTY_ACCOUNT_IDS: number[] = [];

/**
 * Zustand store for managing selected accounts in pair statistic panel.
 * Persists selection per pair ID to localStorage for future balance chart integration.
 */
export const usePairStatisticStore = create<PairStatisticState>()(
  persist(
    (set, get) => ({
      selectedAccountIdsByPair: {},

      setSelectedAccounts: (pairId: number, accountIds: number[]) => {
        set(state => ({
          selectedAccountIdsByPair: {
            ...state.selectedAccountIdsByPair,
            [pairId]: accountIds,
          },
        }));
      },

      getSelectedAccountIds: (pairId: number) => {
        return get().selectedAccountIdsByPair[pairId] ?? [];
      },

      clearSelectedAccounts: (pairId: number) => {
        set(state => {
          const { [pairId]: _, ...rest } = state.selectedAccountIdsByPair;
          return { selectedAccountIdsByPair: rest };
        });
      },

      clearAll: () => {
        set({ selectedAccountIdsByPair: {} });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: state => ({
        selectedAccountIdsByPair: state.selectedAccountIdsByPair,
      }),
    },
  ),
);

/**
 * Hook to get selected accounts for a specific pair
 * Filters the full accounts list by stored selected IDs
 */
export function useSelectedAccounts(
  pairId: number,
  accounts: ICexAccount[],
): {
  selectedAccounts: ICexAccount[];
  setSelectedAccounts: (accounts: ICexAccount[]) => void;
} {
  // Use stable empty array reference to prevent infinite re-renders
  const selectedIds = usePairStatisticStore(
    state => state.selectedAccountIdsByPair[pairId] ?? EMPTY_ACCOUNT_IDS,
  );
  const setSelectedAccountIds = usePairStatisticStore(state => state.setSelectedAccounts);

  // Memoize selected accounts to avoid unnecessary recalculations
  const selectedAccounts = useMemo(
    () => (selectedIds.length > 0 ? accounts.filter(acc => selectedIds.includes(acc.id)) : []),
    [accounts, selectedIds],
  );

  // Memoize the setter to prevent infinite loops in consumers
  const setSelectedAccounts = useCallback(
    (selected: ICexAccount[]) => {
      setSelectedAccountIds(
        pairId,
        selected.map(acc => acc.id),
      );
    },
    [pairId, setSelectedAccountIds],
  );

  return {
    selectedAccounts,
    setSelectedAccounts,
  };
}
